import styled, { keyframes } from 'styled-components';

import RewardStepAccountSvg from '../../../assets/icons/tip-reward-step-account.svg?react';
import RewardStepDonationSvg from '../../../assets/icons/tip-reward-step-donation.svg?react';
import RewardStepGiftShellSvg from '../../../assets/icons/tip-reward-step-gift-shell.svg?react';
import profilePhotoImage from '../../../assets/images/registration-complete-avatar-photo.png';
import rewardStepGiftInnerImage from '../../../assets/images/tip-reward-step-gift-inner.png';
import { BaseButton, rubikTypographyStyles } from '../../../shared/ui';

const REWARD_PAGE_PAUSE_MS = 180;
const REWARD_AMOUNT_REVEAL_DURATION_MS = 520;
const REWARD_AMOUNT_TO_BURST_GAP_MS = 90;
const REWARD_AMOUNT_BURST_WINDOW_MS = 760;
const REWARD_BURST_TO_HEART_GAP_MS = 120;
const REWARD_HEART_DRAW_DURATION_MS = 860;
const REWARD_HEART_TO_BURST_GAP_MS = 110;

const REWARD_AMOUNT_REVEAL_DELAY_MS = REWARD_PAGE_PAUSE_MS;
const REWARD_BURST_REVEAL_DELAY_MS =
  REWARD_AMOUNT_REVEAL_DELAY_MS +
  REWARD_AMOUNT_REVEAL_DURATION_MS +
  REWARD_AMOUNT_TO_BURST_GAP_MS;
const REWARD_HEART_REVEAL_DELAY_MS =
  REWARD_BURST_REVEAL_DELAY_MS +
  REWARD_AMOUNT_BURST_WINDOW_MS +
  REWARD_BURST_TO_HEART_GAP_MS;
const REWARD_HEART_FIREWORK_DELAY_MS =
  REWARD_HEART_REVEAL_DELAY_MS +
  REWARD_HEART_DRAW_DURATION_MS +
  REWARD_HEART_TO_BURST_GAP_MS;

const rewardAmountReveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-18px) scale(0.92);
  }

  64% {
    opacity: 1;
    transform: translateY(4px) scale(1.02);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const rewardDotPulseReveal = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.2);
  }

  45% {
    opacity: 1;
    transform: scale(1.16);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const rewardTrailDrawReveal = keyframes`
  0% {
    opacity: 0;
    stroke-dashoffset: 1;
    transform: translate(4px, 4px) scale(0.88);
  }

  20% {
    opacity: 1;
  }

  100% {
    opacity: 1;
    stroke-dashoffset: 0;
    transform: translate(0, 0) scale(1);
  }
`;

const rewardHeartDrawReveal = keyframes`
  0% {
    opacity: 0;
    stroke-dashoffset: 1;
  }

  12% {
    opacity: 1;
  }

  100% {
    opacity: 1;
    stroke-dashoffset: 0;
  }
`;

const rewardHeartFireworkDotReveal = keyframes`
  0% {
    opacity: 0;
    transform: translate(
        var(--reward-heart-firework-x, 0),
        var(--reward-heart-firework-y, 0)
      )
      scale(0.18);
  }

  58% {
    opacity: 1;
    transform: translate(0, 0) scale(1.12);
  }

  100% {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
`;

const rewardHeartFireworkTrailReveal = keyframes`
  0% {
    opacity: 0;
    stroke-dashoffset: 1;
    transform: translate(
        var(--reward-heart-firework-x, 0),
        var(--reward-heart-firework-y, 0)
      )
      scale(0.82);
  }

  22% {
    opacity: 1;
  }

  68% {
    transform: translate(0, 0) scale(1.04);
  }

  100% {
    opacity: 1;
    stroke-dashoffset: 0;
    transform: translate(0, 0) scale(1);
  }
`;

export const Screen = styled.div.attrs({
  'data-ui': 'tip-reward-claim-screen',
})`
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;

  & > [data-ui='tip-flow-top-bar'] {
    flex-shrink: 0;
  }
`;

export const Journey = styled.div.attrs({
  'data-ui': 'tip-reward-claim-journey',
})`
  position: relative;
  width: 100%;
  height: ${({ theme }) => theme.scale.px(74)};
  margin-top: ${({ theme }) => theme.scale.px(25)};
  overflow: visible;
  flex-shrink: 0;
`;

export const JourneyConnector = styled.div<{
  $isCompleted?: boolean;
  $left: number;
  $width: number;
}>`
  position: absolute;
  left: ${({ theme, $left }) => theme.scale.px($left)};
  top: ${({ theme }) => theme.scale.px(26)};
  width: ${({ theme, $width }) => theme.scale.px($width)};
  height: ${({ theme }) => theme.scale.px(2)};
  background: ${({ $isCompleted, theme }) =>
    $isCompleted ? theme.colors.brand.primary : '#979797'};
`;

export const StepDonationIcon = styled(RewardStepDonationSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  position: absolute;
  left: ${({ theme }) => theme.scale.px(13)};
  top: ${({ theme }) => theme.scale.px(4)};
  width: ${({ theme }) => theme.scale.px(37)};
  height: ${({ theme }) => theme.scale.px(39)};
  display: block;
`;

export const StepAccountIcon = styled(RewardStepAccountSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  position: absolute;
  left: ${({ theme }) => theme.scale.px(156)};
  top: ${({ theme }) => theme.scale.px(9)};
  width: ${({ theme }) => theme.scale.px(35)};
  height: ${({ theme }) => theme.scale.px(35)};
  display: block;
`;

export const StepGiftIconWrap = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.scale.px(291)};
  top: 0;
  width: ${({ theme }) => theme.scale.px(52)};
  height: ${({ theme }) => theme.scale.px(52)};
`;

export const StepGiftShell = styled(RewardStepGiftShellSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
`;

export const StepGiftIcon = styled.img.attrs({
  alt: '',
  src: rewardStepGiftInnerImage,
})`
  position: absolute;
  left: ${({ theme }) => theme.scale.px(5)};
  top: ${({ theme }) => theme.scale.px(2)};
  width: ${({ theme }) => theme.scale.px(42)};
  height: ${({ theme }) => theme.scale.px(48)};
  display: block;
`;

export const JourneyLabel = styled.span<{
  $left: number;
  $tone: 'completed' | 'active' | 'upcoming';
  $width: number;
}>`
  ${rubikTypographyStyles};
  position: absolute;
  display: block;
  left: ${({ theme, $left }) => theme.scale.px($left)};
  top: ${({ theme }) => theme.scale.px(63)};
  width: ${({ theme, $width }) => theme.scale.px($width)};
  text-align: center;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.scale.px(9)};
  font-weight: 600;
  line-height: 1.25;
  color: ${({ $tone, theme }) => {
    if ($tone === 'completed') {
      return 'rgba(0, 0, 0, 0.3)';
    }

    if ($tone === 'upcoming') {
      return 'rgba(0, 0, 0, 0.5)';
    }

    return theme.colors.text.primary;
  }};
`;

export const Title = styled.h1`
  ${rubikTypographyStyles};
  width: ${({ theme }) => theme.scale.px(341)};
  margin: ${({ theme }) => `${theme.scale.px(121)} 0 0 ${theme.scale.px(1)}`};
  align-self: flex-start;
  font-size: ${({ theme }) => theme.scale.px(24)};
  line-height: 1.18;
  font-weight: 400;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

export const TitleLead = styled.span`
  font-weight: 600;
`;

export const TitleRest = styled.span`
  font-weight: 400;
`;

export const RewardStage = styled.div`
  position: relative;
  width: 100%;
  height: ${({ theme }) => theme.scale.px(290)};
  margin-top: ${({ theme }) => theme.scale.px(20)};
  overflow: visible;
  flex-shrink: 0;
`;

export const AvatarFrame = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.scale.px(42)};
  top: 0;
  width: ${({ theme }) => theme.scale.px(110)};
  height: ${({ theme }) => theme.scale.px(110)};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface.tertiary};
  overflow: hidden;
  display: grid;
  place-items: center;
`;

export const AvatarImage = styled.img.attrs({
  alt: '',
  src: profilePhotoImage,
})`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
`;

export const RewardLabel = styled.p`
  ${rubikTypographyStyles};
  position: absolute;
  left: ${({ theme }) => theme.scale.px(217)};
  top: ${({ theme }) => theme.scale.px(6)};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(14)};
  font-weight: 600;
  line-height: 1.18;
`;

export const RewardAmountCluster = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.scale.px(128)};
  top: ${({ theme }) => theme.scale.px(27)};
  width: ${({ theme }) => theme.scale.px(168)};
  height: ${({ theme }) => theme.scale.px(79)};
  pointer-events: none;
`;

export const RewardAmountText = styled.div`
  ${rubikTypographyStyles};
  position: absolute;
  left: ${({ theme }) => theme.scale.px(40)};
  top: ${({ theme }) => theme.scale.px(18)};
  display: flex;
  align-items: baseline;
  gap: 0;
  color: #050505;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.scale.px(48)};
  font-weight: 600;
  line-height: normal;
  animation: ${rewardAmountReveal} ${REWARD_AMOUNT_REVEAL_DURATION_MS}ms
    cubic-bezier(0.2, 0.9, 0.28, 1) both;
  animation-delay: ${REWARD_AMOUNT_REVEAL_DELAY_MS}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const RewardAmountPlus = styled.span`
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
`;

export const RewardAmountValue = styled.span`
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  letter-spacing: normal;
`;

export const RewardAmountBurstSvg = styled.svg.attrs({
  'aria-hidden': true,
  focusable: 'false',
  viewBox: '0 0 101.08 78.6619',
})`
  position: absolute;
  left: ${({ theme }) => theme.scale.px(67)};
  top: 0;
  width: ${({ theme }) => theme.scale.px(101)};
  height: ${({ theme }) => theme.scale.px(79)};
  display: block;

  .reward-burst-dot {
    opacity: 0;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${rewardDotPulseReveal} 280ms ease-out forwards;
  }

  .reward-burst-dot--one {
    animation-delay: ${REWARD_BURST_REVEAL_DELAY_MS + 140}ms;
  }

  .reward-burst-dot--two {
    animation-delay: ${REWARD_BURST_REVEAL_DELAY_MS + 230}ms;
  }

  .reward-burst-dot--three {
    animation-delay: ${REWARD_BURST_REVEAL_DELAY_MS + 320}ms;
  }

  .reward-burst-trail {
    opacity: 0;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${rewardTrailDrawReveal} 420ms cubic-bezier(0.2, 0.8, 0.22, 1)
      forwards;
  }

  .reward-burst-trail--one {
    animation-delay: ${REWARD_BURST_REVEAL_DELAY_MS + 40}ms;
  }

  .reward-burst-trail--two {
    animation-delay: ${REWARD_BURST_REVEAL_DELAY_MS + 130}ms;
  }

  .reward-burst-trail--three {
    animation-delay: ${REWARD_BURST_REVEAL_DELAY_MS + 240}ms;
  }

  .reward-burst-trail--four {
    animation-delay: ${REWARD_BURST_REVEAL_DELAY_MS + 340}ms;
  }

  @media (prefers-reduced-motion: reduce) {
    .reward-burst-dot,
    .reward-burst-trail {
      animation: none;
      opacity: 1;
      transform: none;
      stroke-dashoffset: 0;
    }
  }
`;

export const RewardHeartArrowSvg = styled.svg.attrs({
  'aria-hidden': true,
  focusable: 'false',
  viewBox: '0 0 109.608 84.7936',
})`
  position: absolute;
  left: ${({ theme }) => theme.scale.px(128)};
  top: ${({ theme }) => theme.scale.px(207)};
  width: ${({ theme }) => theme.scale.px(110)};
  height: ${({ theme }) => theme.scale.px(83)};
  display: block;

  .reward-heart-dot {
    opacity: 0;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${rewardHeartFireworkDotReveal} 420ms ease-out forwards;
  }

  .reward-heart-dot--one {
    --reward-heart-firework-x: -12px;
    --reward-heart-firework-y: 8px;
    animation-delay: ${REWARD_HEART_FIREWORK_DELAY_MS + 170}ms;
  }

  .reward-heart-dot--two {
    --reward-heart-firework-x: 10px;
    --reward-heart-firework-y: -9px;
    animation-delay: ${REWARD_HEART_FIREWORK_DELAY_MS + 270}ms;
  }

  .reward-heart-trail {
    opacity: 0;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${rewardHeartFireworkTrailReveal} 520ms
      cubic-bezier(0.2, 0.8, 0.22, 1) forwards;
  }

  .reward-heart-trail--one {
    --reward-heart-firework-x: -14px;
    --reward-heart-firework-y: 10px;
    animation-delay: ${REWARD_HEART_FIREWORK_DELAY_MS}ms;
  }

  .reward-heart-trail--two {
    --reward-heart-firework-x: 12px;
    --reward-heart-firework-y: 10px;
    animation-delay: ${REWARD_HEART_FIREWORK_DELAY_MS + 110}ms;
  }

  .reward-heart-main {
    opacity: 0;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    animation: ${rewardHeartDrawReveal} ${REWARD_HEART_DRAW_DURATION_MS}ms
      cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
    animation-delay: ${REWARD_HEART_REVEAL_DELAY_MS}ms;
  }

  @media (prefers-reduced-motion: reduce) {
    .reward-heart-dot,
    .reward-heart-trail,
    .reward-heart-main {
      animation: none;
      opacity: 1;
      transform: none;
      stroke-dashoffset: 0;
    }
  }
`;

export const Actions = styled.div`
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.scale.px(13)};
  margin-top: ${({ theme }) => theme.scale.px(17)};
`;

export const AccountLink = styled(BaseButton)`
  ${rubikTypographyStyles};
  font-size: ${({ theme }) => theme.scale.px(15)};
  line-height: 1.25;
  text-decoration: underline;
  text-decoration-thickness: ${({ theme }) => theme.scale.px(1)};
  text-underline-offset: ${({ theme }) => theme.scale.px(3)};
`;
